import { useContext, useEffect, useState } from "react";
import { ErrorContext } from "../../../context/ErrorContext";
import { AuthContext } from "../../../context/AuthContext";
import { CouponRate, CouponRateType } from "../../../collector";
import { Button, Modal } from "react-bootstrap";
import Switch from "../../../components/Switch/Switch";
import EnabledLabel from "../../../components/EnabledLabel";

interface EditCouponRateProps {
    editedCouponRate: () => void;
    environment: string;
    couponRateId: number | null;
    close: () => void;
}

export default function EditCouponRateModal(props: EditCouponRateProps) {
    const [rate, setRate] = useState<CouponRate | null>(null);
    const [show, setShow] = useState<boolean>(false);
    const { showErrorModal } = useContext(ErrorContext);
    const { token } = useContext(AuthContext);

    useEffect(() => {
        if (props.couponRateId) {
            fetchRate();
            handleShow();
        }
    }, [props.couponRateId])

    const handleClose = () => {
        setRate(null);
        props.close();
        setShow(false);
    }
    const handleShow = () => setShow(true);

    const editRate = async () => {
        try {
            if (rate === null) throw Error('Coupon Rate is null...');
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token(),
                },
                body: JSON.stringify({
                    'id': rate.id,
                    'start': rate.start ?? null,
                    'end': rate.end ?? null,
                    'enabled': rate.enabled,
                    'max_uses': rate.max_uses,
                    'type': rate.type,
                    'value': rate.value,
                    'description': rate.description,
                    'invoice_line': rate.invoice_line
                })
            }
            await fetch(props.environment + 'editRate', requestOptions)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Failed to update rate");
                    }
                    return res.json();
                })
                .then(() => {
                    props.editedCouponRate();
                    handleClose();
                });
        } catch (e: any) {
            showErrorModal(e.message ?? "Failed to update rate - Server is unavailable at the moment");
        }
    }

    const fetchRate = async () => {
        try {
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token(),
                },
                body: JSON.stringify({
                    'id': props.couponRateId
                })
            }
            await fetch(props.environment + 'getRate', requestOptions)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Failed to fetch rate");
                    }
                    return res.json();
                })
                .then(({ rate }) => {
                    setRate(rate);
                });
        } catch (e: any) {
            showErrorModal(e.message ?? "Failed to fetch rate - Server is unavailable at the moment");
            handleClose();
        }
    }

    return (
        <>
            {show && rate &&
                <Modal
                    show={show}
                    backdrop="static"
                    onHide={() => handleClose()}
                    keyboard={false}
                >
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        editRate();
                    }}>
                        <Modal.Header closeButton>
                            <Modal.Title>Edit Rate</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="dialog-data-field">
                                <span>Type:</span>
                                <select value={rate.type}
                                    onChange={(item) => {
                                        let temp = { ...rate };
                                        temp.type = item.target.value as CouponRateType;
                                        if (temp.type === CouponRateType.Percentage && temp.value > 99) {
                                            temp.value = 99;
                                        }
                                        setRate(temp);
                                    }}
                                >
                                    <option value={CouponRateType.Fixed}>Fixed</option>
                                    <option value={CouponRateType.Percentage}>Percentage %</option>
                                </select>
                            </div>
                            <div className="dialog-data-field">
                                <span>Value:</span>
                                <input type='numbers' placeholder="Write value here..." min={1} value={rate.value} required
                                    onChange={(item) => {
                                        if (isNaN(Number(item.target.value))) return;
                                        let tooHigh = (rate.type === CouponRateType.Percentage && Number(item.target.value) >= 100);
                                        setRate({ ...rate, value: (tooHigh ? 99 : Number(item.target.value)) });
                                    }} />
                            </div>
                            <div className="dialog-data-field">
                                <span>Max uses: (0 is inifity)</span>
                                <input type='numbers' placeholder="Write max uses here..." min={0} value={rate.max_uses} required
                                    onChange={(item) => setRate({ ...rate, max_uses: Number(item.target.value) })} />
                            </div>
                            <div className="dialog-data-field">
                                <span>Start time:</span>
                                <input type='datetime-local' defaultValue={rate.start ?? undefined} required={rate.end ? true : false}
                                    onChange={(item) => setRate({ ...rate, start: item.target.value })} />
                            </div>
                            <div className="dialog-data-field">
                                <span>End time:</span>
                                <input type='datetime-local' defaultValue={rate.end ?? undefined} required={rate.start ? true : false}
                                    onChange={(item) => setRate({ ...rate, end: item.target.value })} />
                            </div>
                            <div className="dialog-data-field">
                                <span><em>Note: To use start- and end time, one must give both fields a value</em></span>
                            </div>
                            <div className="dialog-data-field">
                                <span>Invoice line:</span>
                                <input type="text" placeholder="Write invoice line here..." value={rate.invoice_line ?? ""} required
                                    onChange={(item) => setRate({ ...rate, invoice_line: item.target.value })} />
                            </div>
                            <div className="dialog-data-field">
                                <span>Enabled:</span>
                                <Switch checked={rate.enabled} onChange={(item) => setRate({ ...rate, enabled: item.target.checked })} />
                            </div>
                            <div className="text-end-container">
                                <EnabledLabel enabled={rate.enabled} />
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => handleClose()}>
                                Close
                            </Button>
                            <Button variant="primary" type='submit'>Save</Button>
                        </Modal.Footer>
                    </form>
                </Modal>
            }
        </>
    );
}